import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, fmtEur, fmtDate, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { QUOTE_STATUS_COLORS, normalizeLine } from "../lib/fields";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { CommentsAudit } from "./CommentsAudit";
import { SendEmailDialog } from "./SendEmailDialog";
import { LineItemsEditor } from "./LineItemsEditor";
import { RfqDocuments } from "./RfqDocuments";
import { toast } from "sonner";
import { FileDown, GitBranch, Save, Mail, Package } from "lucide-react";

/**
 * Full quote record view (header, status, PDF, e-mail, revise, create-order, RFQ documents,
 * line items + save, comments/audit). Reused by the Quotes detail page and inline on the
 * Account 360° page. `onChanged` fires after a mutation so the caller can refresh.
 */
export function QuoteDetailView({ quoteId, onChanged }) {
  const navigate = useNavigate();
  const { t, canWrite, isManager, plOptions } = useApp();
  const [q, setQ] = useState(null);
  const [lines, setLines] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);

  const load = useCallback(() =>
    api.get(`/quotes/${quoteId}`).then((r) => { setQ(r.data); setLines((r.data.line_items || []).map(normalizeLine)); }).catch(() => {}),
  [quoteId]);
  useEffect(() => { load(); }, [load]);

  if (!q) return <p className="text-gray-400">{t("loading")}</p>;

  const notifyChanged = () => onChanged && onChanged();

  const save = async (extra = {}) => {
    try {
      const r = await api.put(`/quotes/${quoteId}`, { ...q, line_items: lines, ...extra });
      setQ(r.data); setLines((r.data.line_items || []).map(normalizeLine));
      toast.success(t("save") + " ✓"); notifyChanged();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const revise = async () => {
    try {
      const r = await api.post(`/quotes/${quoteId}/revise`);
      toast.success(`${t("revise")}: v${r.data.version}`);
      navigate(`/quotes/${r.data.id}`);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const createOrder = async () => {
    try {
      const r = await api.post(`/quotes/${quoteId}/create-order`);
      toast.success(`${t("ord_created")}: ${r.data.order_number}`);
      navigate(`/orders/${r.data.id}`);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("lf_token");
      const res = await fetch(`${API}/quotes/${quoteId}/pdf`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${q.quote_number}_v${q.version}.pdf`;
      a.click();
    } catch { toast.error("PDF error"); }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight font-mono text-[#1B2A4A]" style={{ fontFamily: "Work Sans" }} data-testid="quote-number">
              {q.quote_number} <span className="text-gray-400 text-lg">v{q.version}</span>
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-gray-500">
              <button className="text-blue-600 hover:underline" onClick={() => navigate(`/accounts/${q.account_id}`)}>{q.account_name}</button>
              ·<Badge className={`${QUOTE_STATUS_COLORS[q.status] || ""} hover:bg-inherit`}>{q.status}</Badge>
              ·<span>{t("validity")}: {fmtDate(q.validity_date)}</span>·<span>{q.incoterm}</span>·<span>{q.payment_terms}</span>
            </div>
            {q.opportunity_name && <p className="text-xs text-gray-400 mt-1">↳ {q.opportunity_name}</p>}
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">{t("total")}</p>
            <p className="text-2xl font-bold font-mono text-[#1B2A4A]" data-testid="quote-total">{fmtEur(q.total, q.currency)}</p>
            {isManager && <p className="text-sm font-mono text-emerald-600" data-testid="quote-margin">{t("margin")}: {q.margin_pct}%</p>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {canWrite && (
            <Select value={q.status} onValueChange={(v) => save({ status: v })}>
              <SelectTrigger className="w-48 h-9" data-testid="quote-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{plOptions("quote_statuses").map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button size="sm" variant="outline" onClick={downloadPdf} data-testid="quote-pdf-btn">
            <FileDown className="h-3.5 w-3.5 mr-1" />{t("download_pdf")}
          </Button>
          {canWrite && (
            <Button size="sm" className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={() => setEmailOpen(true)} data-testid="quote-email-btn">
              <Mail className="h-3.5 w-3.5 mr-1" />{t("send_email")}
            </Button>
          )}
          {canWrite && (
            <Button size="sm" variant="outline" onClick={revise} data-testid="quote-revise-btn">
              <GitBranch className="h-3.5 w-3.5 mr-1" />{t("revise")}
            </Button>
          )}
          {canWrite && (
            <Button size="sm" className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold" onClick={createOrder} data-testid="quote-create-order-btn">
              <Package className="h-3.5 w-3.5 mr-1" />{t("quote_create_order")}
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <RfqDocuments module="quotes" itemId={quoteId} canWrite={canWrite}
          onAddLines={(newLines) => { setLines((ls) => [...ls, ...newLines]); toast.success(t("save") + " ✓"); }} />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <LineItemsEditor lines={lines} setLines={setLines} canWrite={canWrite} currency={q.currency} />
        {canWrite && (
          <Button className="mt-4 bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={() => save()} data-testid="quote-save-btn">
            <Save className="h-4 w-4 mr-1.5" />{t("save")}
          </Button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <CommentsAudit module="quotes" id={quoteId} />
      </div>

      <SendEmailDialog open={emailOpen} onOpenChange={setEmailOpen}
        endpoint={`/quotes/${quoteId}/send-email`}
        defaultSubject={`Angebot ${q.quote_number} (v${q.version}) – Encasa`}
        onSent={load} />
    </div>
  );
}

/** Dialog wrapper so a quote can be opened in place (e.g. on the Account 360° page). */
export function QuoteDetailDialog({ quote, onClose, onChanged }) {
  const { t } = useApp();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="quote-detail-dialog">
        <DialogHeader>
          <DialogTitle>{t("quotes")} · {quote.quote_number}</DialogTitle>
          <DialogDescription>{quote.account_name}</DialogDescription>
        </DialogHeader>
        <QuoteDetailView quoteId={quote.id} onChanged={onChanged} />
      </DialogContent>
    </Dialog>
  );
}
